import React, { useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Grid, TextField } from '@mui/material';
import { Refresh, Search } from "@mui/icons-material";
import Button from "@mui/material/Button";
import { toast } from 'react-toastify';
import { InvalidMessage, GeneralError, accountInputLen, memberInputLen } from "../../Utility/Constants";
import Table from '../../Table/Table';
import { PACompoundDetailColumns } from '../../Table/TableColumn';
import { tableColumns } from '../../Redux/TableSlice';
import { currentData, clearData, updateSearch, searchClicked, updateInputError } from '../../Redux/MNR/PACompoundDetailSlice';
import { clearAll, searchTriggered } from '../../Redux/CNS/CommonSlice';
import {
    validateRegexWithNumbers, validateRegexAlphaNumeric
} from '../../Validation/Validation';
import Spinner from '../../Spinner/Spinner';
import { showAll, isMNR } from '../../Redux/MNR/MNRCommonSlice';

function PACompoundDetail() {
    const dispatch = useDispatch();
    const error = useSelector(state => state.paCompound.error);
    const isSearched = useSelector(state => state.paCompound.isSearchClicked);
    const searchTriggeredValue = useSelector(state => state.cnsCommon.searchTriggered);
    const inputError = useSelector(state => state.paCompound.inputError);
    const hasAnyError = Object.values(inputError).some(value => value === true);
    const data = useSelector(state => state.paCompound.data);
    const isSearchEnable = ((data.Account && data.Account.trim() !== '') ||
        (data.Group && data.Group.trim() !== '') ||
        (data.GPI && data.GPI.trim() !== '') ||
        (data.MemberID && data.MemberID.trim() !== '') ||
        (data.PANumber && data.PANumber.trim() !== '') ||
        (data.Carrier && data.Carrier.trim() !== ''));
    const loading = useSelector(state => state.paCompound.loading);
    const showAllValue = useSelector(state => state.mnrCommon.showAll);

    useEffect(() => {
        if (error) {
            toast.error(GeneralError);
            return;
        }
    }, [error]);

    const handleResetInitial = (useCallback(() => {
        dispatch(clearAll());
        dispatch(clearData());
        dispatch(updateSearch(true));
        dispatch(searchClicked(false));
        dispatch(showAll(false));
        dispatch(isMNR(true));
    }, [dispatch]))

    const handleReset = () => {
        dispatch(clearData());
        dispatch(updateSearch(true));
        dispatch(searchClicked(false));
        dispatch(showAll(false));
    }

    useEffect(() => {
        handleResetInitial();
        dispatch(tableColumns(PACompoundDetailColumns));
    }, [dispatch, handleResetInitial])

    const handleChange = (name, value) => {
        const val = value.target.value || value.target.innerText;
        switch (name) {
            case 'PANumber':
                //const updateRegexWithNumber = validateRegexNumbersCharSpecialCharLong(name, val, inputError);
                //dispatch(updateInputError(updateRegexWithNumber));
                break;
            case 'Account':
                const updateRegexIncludeNumbers = validateRegexWithNumbers(name, val, inputError);
                dispatch(updateInputError(updateRegexIncludeNumbers));
                break;
            case 'Group':
                const updateRegexInclNumbers = validateRegexWithNumbers(name, val, inputError);
                dispatch(updateInputError(updateRegexInclNumbers));
                break;
             case 'Carrier':
                const updateRegexCarrierAlphaNumeric = validateRegexAlphaNumeric({
                    name,
                    value: val,
                    inputError,
                    maxLength: accountInputLen
                });
                dispatch(updateInputError(updateRegexCarrierAlphaNumeric));
                break;
            case 'MemberID':
                const updateRegexMemberAlphaNumeric = validateRegexAlphaNumeric({
                    name,
                    value: val,
                    inputError,
                    maxLength: memberInputLen
                });
                dispatch(updateInputError(updateRegexMemberAlphaNumeric));
                break;
            default:
                break;
        }

        const updatedData = {
            ...data,
            [name]: val
        };
        dispatch(currentData(updatedData));
    }

    const handleSearch = () => {
        dispatch(showAll(false));
        dispatch(searchClicked(true));
        dispatch(searchTriggered(!searchTriggeredValue));
    }

    const handleShowAll = () => {
        dispatch(showAll(true));
        dispatch(clearData());
        dispatch(searchClicked(false));
        dispatch(searchTriggered(!searchTriggeredValue));
    }

    return (
        <>
            {loading && <Spinner />}
            <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6, md: 1.714285714285714, lg: 1.714285714285714, xl: 1.714285714285714 }}>
                    <TextField
                        id="memberID"
                        label="MemberID"
                        variant="outlined"
                        size="small"
                        onChange={(e) => handleChange('MemberID', e)}
                        value={data.MemberID}
                        error={inputError.MemberID}
                        helperText={inputError.MemberID ? InvalidMessage : ''}
                        fullWidth
                    />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 1.714285714285714, lg: 1.714285714285714, xl: 1.714285714285714 }}>
                    <TextField
                        id="carrier"
                        label="Carrier"
                        variant="outlined"
                        size="small"
                        onChange={(e) => handleChange('Carrier', e)}
                        value={data.Carrier}
                        error={inputError.Carrier}
                        helperText={inputError.Carrier ? InvalidMessage : ''}
                        fullWidth
                    />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 1.714285714285714, lg: 1.714285714285714, xl: 1.714285714285714 }}>
                    <TextField
                        id="account"
                        label="Account"
                        variant="outlined"
                        size="small"
                        onChange={(e) => handleChange('Account', e)}
                        value={data.Account}
                        error={inputError.Account}
                        helperText={inputError.Account ? InvalidMessage : ''}
                        fullWidth
                    />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 1.714285714285714, lg: 1.714285714285714, xl: 1.714285714285714 }}>
                    <TextField
                        id="group"
                        label="Group"
                        variant="outlined"
                        size="small"
                        onChange={(e) => handleChange('Group', e)}
                        value={data.Group}
                        error={inputError.Group}
                        helperText={inputError.Group ? InvalidMessage : ''}
                        fullWidth
                    />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 1.714285714285714, lg: 1.714285714285714, xl: 1.714285714285714 }}>
                    <TextField
                        id="paNumber"
                        label="PANumber"
                        variant="outlined"
                        size="small"
                        onChange={(e) => handleChange('PANumber', e)}
                        value={data.PANumber}
                        error={inputError.PANumber}
                        helperText={inputError.PANumber ? InvalidMessage : ''}
                        fullWidth
                    />
                </Grid>
            </Grid>
            <Grid container spacing={3} sx={{ justifyContent: 'center', paddingTop: '24px' }}>
                <Button
                    variant="contained"
                    onClick={handleShowAll}
                >
                    Show All
                </Button>
                <Button
                    variant={(!isSearched && !showAllValue) ? "contained" : "outlined"}
                    style={isSearched ? { color: 'var(--primary-font-color)' } : undefined}
                    size="small"
                    onClick={handleReset}
                    endIcon={<Refresh />}
                    disabled={!isSearched && !showAllValue}
                >
                    Reset
                </Button>
                <Button
                    variant={(!isSearchEnable || hasAnyError) ? "contained" : "contained"}
                    size="large"
                    onClick={handleSearch}
                    endIcon={<Search />}
                    style={(isSearchEnable && !hasAnyError) ? { color: 'var(--primary-font-color)', background: 'var(--primary-color)', border: '1px solid var(--primary-font-color)' } : undefined}
                    disabled={!isSearchEnable || hasAnyError}
                >
                    Search
                </Button>
            </Grid>
            {((isSearched) || (showAllValue)) && <Table />}
        </>
    )
}

export default PACompoundDetail;
